<template>
  <div class="auth-page-container h-full flex flex-col">
    <!-- Loading Spinner while page is still loading -->
    <ProgressSpinner v-if="loading" class="loading-spinner" animationDuration=".8s" style="width: 64px; height: 64px" strokeWidth="2" fill="transparent"/>

    <!-- Page Content -->
    <ContainerMain v-else class="flex-grow h-full">
      <BlockHeader :additionalClasses="'pb-[3px]'" :show-back-to-auth-profile="true" :show-login-link="true"/>

      <div class="profile-container flex items-center">
        <div class="info-section pt-5">
          <div v-if="!loading">
            <h1 class="text-3xl md:text-4xl font-bold mt-6 mb-0 md:mt-8 md:mb-0 pt-[7px] pb-[6px]">
				<template v-if="!isAnonymous">{{ profileData.name }}</template>
			</h1>
            <p v-if="profileData?.functional_role && functionalRoles?.length > 0" class="text-xl font-bold pt-[1px] inline-block">
              <MultilanguageDisplay :value="getFunctionalRoleName(profileData.functional_role)"/>
            </p>

						<span class="inline-block items-center ml-[8px]"
									v-if="profileData?.country_code && profileData?.country_name">
							<img
								:src="`/images/flags/${profileData?.country_code.toLowerCase()}.svg`"
								:alt="localizedCountryName"
								class="ml-0 mt-[2px] w-[16px] h-[12px] rounded-[2px]"
								v-tooltip.top="{
								value: localizedCountryName,
								pt: {
											arrow: {
													style: {
															borderBottomColor: '!bg-[#202425]'
													}
											},
											text: '!bg-[#202425] !text-sm'
									}
								}"
							/>
						</span>

            <div class="pt-[7px]">
              <!-- Social Profiles Section -->
              <div v-if="mappedSocialProfiles" class="!mb-[3px]">
                <div v-for="profile in mappedSocialProfiles" :key="profile.key"
                     class="!pt-[4px] pb-[4px] flex items-center">
                  <img
                    :src="`/images/social/${profile.icon}`"
                    :alt="profile.name"
                    class="w-[16px] h-[16px] mr-2"
                  />
                  <a :href="profile.link" target="_blank" rel="noopener noreferrer"
                     class="font-bold text-base text-black">
                    {{ profile.name }}
                  </a>
                </div>
								<template v-if="auth.logged && auth?.user.id !== profile?.id">

									<div class="!pt-[4px] pb-[4px] flex items-center" v-if=" auth.user.id  !==  profileData?.id">
										<img
											src="/images/elements/email.svg"
											alt="Send message"
											class="w-[16px] h-[16px] mr-2"
										/>
										<a href="#" target="_blank" rel="noopener noreferrer" @click.prevent="showMessageDialog = true"
											 class="font-bold text-base">
											{{ t('send_message') }}
										</a>
									</div>
								</template>
              </div>
            </div>
          </div>
        </div>

        <!-- Avatar section -->
        <div class="avatar-section mr-[0px] pt-5">
          <Transition name="fade" mode="out-in" appear>
            <div v-if="!loading">
              <template v-if="!isAnonymous && $storage(profileData.avatar)">
                <img :src="$storage(profileData.avatar)"
                     class="w-[56px] h-[56px] md:w-[192px] md:h-[192px] rounded-full filled-upload mr-0" alt=""/>
              </template>
              <template v-else>
								<DefaultAvatar
									:width="avatarSize.width"
									:height="avatarSize.height"
									:width-inner="avatarSize.width_inner"
									:height-inner="avatarSize.height_inner"
									class="w-[56px] h-[56px] md:w-[192px] md:h-[192px] rounded-full filled-upload mr-0"
								/>
              </template>
            </div>
          </Transition>
        </div>
      </div>

      <!-- Activity sky (M4C-34): the member's started constellations -->
      <Transition name="fade" mode="out-in" appear>
        <ActivitySkyView
          v-if="!loading && !isAnonymous && memberConstellations.length > 0"
          :rows="memberConstellations"
          class="pt-4 md:pt-[2rem]"
        />
      </Transition>

      <!-- interestsSection -->
			<template v-if="profileData.selected_interests.length > 0 || profileData.other_interests.length > 0">
				<SectionTitle id="interestsSection" :title="t('profile.interests')"/>
				<Transition name="fade" mode="out-in" appear>
					<div v-if="!loading">
						<YourInterestsView
							:selected-interests="profileData.selected_interests"
							:other-interests="profileData.other_interests"
							:onboardingData="onboardingData"
							:showEdit="false"
							:hideEdit="true"
							:showAdd="false"
						/>
					</div>
				</Transition>
			</template>

			<template v-if="profileData.known_as || profileData.achievements || profileData.selected_professions.length > 0 || profileData.other_professions.length > 0">
				<SectionTitle id="personalitySection" :title="t('profile.personality')"/>
				<Transition name="fade" mode="out-in" appear>
					<div v-if="!loading">
						<PersonalityView
							:known-as="profileData.known_as"
							:achievements="profileData.achievements"
							:selected-professions="profileData.selected_professions"
							:other-professions="profileData.other_professions"
							:onboardingData="onboardingData"
							:showEdit="false"
							:hideEdit="true"
							:showAdd="false"
						/>
					</div>
				</Transition>
			</template>


      <SectionTitle id="transparencySection" :title="t('profile.declaration_of_interests')" v-if="profileData.has_declaration"/>
      <Transition name="fade" mode="out-in" appear>
        <div v-if="profileData.has_declaration && !loading">
          <TransparencyView
            :social-profiles="profileData.social_profiles"
            :show-social-profiles-publicly="profileData.show_social_profiles_publicly"
            :onboardingData="onboardingData"
            :has-declaration="profileData.has_declaration"
            :declaration="profileData.declaration"
            :showEdit="false"
            :hideEdit="true"
						:showAdd="false"
            :showSocialProfiles="false"
          />
        </div>
      </Transition>

    </ContainerMain>

		<Teleport to="body">
			<MessageModal
				v-if="profileData.id"
				:isVisible="showMessageDialog"
				:accountName="profileData.name"
				@update:isVisible="showMessageDialog = $event"
				@sendMessage="handleSendMessage"
				:user-key="profileData.id"
			/>
		</Teleport>
  </div>
</template>

<script lang="ts" setup>
import {ref, onMounted, computed} from 'vue';
import YourInterestsView from "@/components/profile/view/YourInterestsView.vue";
import PersonalityView from "@/components/profile/view/PersonalityView.vue";
import TransparencyView from "@/components/profile/view/TransparencyView.vue";
import ActivitySkyView from "@/components/profile/view/ActivitySkyView.vue";
import SectionTitle from "@/components/content/SectionTitle.vue";
import MultilanguageDisplay from "@/components/core/content/MultilanguageDisplay.vue";
import DefaultAvatar from "@/components/avatar/DefaultAvatar.vue";
import MessageModal from "@/components/notifications/MessageModal.vue";
import {useSettingsStore} from "@/stores/settings";
import { useGamificationService } from "@/composables/gamification";
import type { MemberConstellationRow } from "@/types/gamification";

const { t } = useI18n();

const { locale } = useI18n();

const {$storage} = useNuxtApp();

const settingsStore = useSettingsStore();

const loading = ref(true); // Loading state

const functionalRoles = settingsStore.settings?.functional_roles || [];

const auth = useAuthStore();

const profileData = ref({
  id: '',
  name: '',
  avatar: '',
  selected_interests: [],
  other_interests: [],
  known_as: '',
  achievements: '',
  functional_role: '',
  selected_professions: [],
  other_professions: [],
  social_profiles: [],
  show_social_profiles_publicly: true,
  has_declaration: false,
  declaration: [],
});

const onboardingData = ref({}); // Holds fetched onboarding data

const route = useRoute();
const isAnonymous = computed(() => route.query.anonymous === '1');
const showMessageDialog = ref(false);
const toast = useToast();

// Member gamification rows for the Activity sky section (M4C-34).
const service = useGamificationService();
const memberConstellations = ref<MemberConstellationRow[]>([]);

// Fetch onboarding data
const fetchOnboardingData = async () => {
  const {execute: getOnboardingData} = useFetch('profile/data', {
    method: 'GET',
    immediate: false,
    onResponse: async ({response}) => {
      if (response.status === 200 && response._data?.data) {
        //await settingsStore.fetchPublicSettings();
        onboardingData.value = response._data.data;
      }
    },
    onError: ({error}) => {
      console.error('Failed to fetch onboarding data:', error);
    }
  });

  await getOnboardingData();
};

// Fetch profile data
const fetchProfileData = async () => {
  const {slug} = route.params;

  const {refresh: fetchAuthProfile} = useFetch<any>(`profile-data/${slug}`, {
    immediate: false,
    onResponse: ({response}) => {
      if (response.status === 200) {
        profileData.value = {
          ...response?._data?.data,
        };
      }
    },
    onError: ({error}) => {
      console.error('Error fetching profile data:', error);
    },
  });

  await fetchAuthProfile();
};

// Fetch the member's public gamification rows (M4C-34) for the Activity sky
// section. Renderer-ready rows come from the gamification service (mock today;
// the live community endpoint is a M4C-30 follow-up). Non-blocking: a failure
// hides the section rather than breaking the profile.
const fetchMemberGamification = async () => {
  const memberId = profileData.value?.id;
  if (!memberId || isAnonymous.value) {
    memberConstellations.value = [];
    return;
  }
  try {
    memberConstellations.value = await service.getMemberConstellations(String(memberId));
  } catch (error) {
    console.error('Failed to fetch member gamification:', error);
    memberConstellations.value = [];
  }
};

// Use onMounted to ensure functions are called after component is mounted
onMounted(async () => {
  await fetchOnboardingData();
  await fetchProfileData();
  await fetchMemberGamification();
  loading.value = false; // Set loading to false when all data is loaded
});

// Function to get functional role name from settings
const getFunctionalRoleName = (role) => {
  const functionalRole = settingsStore.settings?.functional_roles.find(fr => fr.key === role);
  return functionalRole ? functionalRole.name : '';
};

// Computed property to map socialProfiles with onboardingData.social_profiles
const mappedSocialProfiles = computed(() => {
  const onboardingProfiles = onboardingData.value.social_profiles || []; // Default to empty array if undefined

  return profileData.value.social_profiles.map((userProfile) => {
    const onboardingProfile = onboardingProfiles.find(
      (profile) => profile.key === userProfile.key
    );
    if (onboardingProfile) {
      return {
        ...onboardingProfile,
        link: userProfile.link
      };
    }
    return null;
  }).filter(profile => profile !== null && profile.link); // Filter out profiles with no link
});

const handleSendMessage = (data) => {
  toast.add({
    severity: "success",
    summary: t('profile.message_sent'),
    life: 3000,
    group: 'tc',
    closable: false
  });
};

const localizedCountryName = computed(() => {
	const raw = profileData.value?.country_name;
	if (!raw) return '';

	// if it in as a JSON string, parse it
	let obj;
	if (typeof raw === 'string') {
		try {
			obj = JSON.parse(raw);
		} catch {
			// not JSON → just return the raw string
			return raw;
		}
	} else {
		obj = raw;
	}

	return obj[locale.value] || obj.en || Object.values(obj)[0] || '';
});


// Reactive avatar size
const avatarSize = ref({ width: 192, height: 192,width_inner: 192, height_inner: 192 });

const handleResize = () => {
	const isMobile = window.matchMedia('(max-width: 768px)').matches;
	avatarSize.value = isMobile ? { width: 56, height: 56,width_inner: 56, height_inner: 56 } : { width: 192, height: 192, width_inner: 192, height_inner: 192 };
};

// Add event listener for resizing
onMounted(() => {
	handleResize(); // Set initial value based on current screen size
	window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize);
});

// Page Meta Information
definePageMeta({
  layout: 'app',
  pageTransition: {
    name: 'slide-right',
    mode: 'out-in'
  },
});

useSeoMeta({
  title: 'Profile',
});
</script>

<style scoped>
.loading-spinner {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.auth-page-container {
  min-height: 100vh;
}

.avatar-section {
	.avatar-container {
		position: relative;
	}
}

.profile-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-height: 192px; /* Avatar height */
}

.info-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center; /* Vertical alignment */
}

.transition-height {
  transition: height 0.7s ease-in-out;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
  opacity: 0;
}
</style>
